'use client';

/**
 * The consent behind `host.rpc('email.brief', { on: true })`: an app asking to
 * email the person using it (lib/email/app-mail.ts). Portal chrome rendered
 * OVER the canvas, never inside the app's frame, twin of
 * {@link WidgetAgentEnsure}: an app cannot switch its own mail on, because
 * nothing is written unless the person confirms here.
 *
 * Opened via a window CustomEvent('widget-app-email') whose detail carries
 * { appName, addressHint, resolve }. The name is the platform's allowlist
 * entry and the address hint is masked ("a•••@gmail.com"); neither comes from
 * the app. The bridge awaits `resolve(true)` on confirm, `resolve(false)` on
 * "Not now", Escape or the backdrop.
 */

import { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';
import { cn } from '@/lib/utils';

interface AppEmailRequest {
  appName: string;
  addressHint: string;
  resolve: (confirmed: boolean) => void;
}

export function WidgetAppEmail() {
  const t = useT();
  const [req, setReq] = useState<AppEmailRequest | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!req);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<AppEmailRequest>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      captureAppFocus();
      // One dialog at a time: a second ask while one is open is a "not now".
      setReq((open) => {
        if (open) { detail.resolve(false); return open; }
        return detail;
      });
    };
    window.addEventListener('widget-app-email', onOpen);
    return () => window.removeEventListener('widget-app-email', onOpen);
  }, [captureAppFocus]);

  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { req.resolve(false); setReq(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [req]);

  if (!req) return null;

  const finish = (confirmed: boolean) => {
    req.resolve(confirmed);
    setReq(null);
  };

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={t('widgetAppEmail.ariaLabel', { app: req.appName })}
      // The panel's place comes from the classes below (a bottom sheet on a
      // phone, a card near the top on a desktop). The alignment was inline
      // too, and an inline style beats `md:items-start`, so on a desktop the
      // dialog sat at the bottom of the page (components/sharing/
      // share-dialog.tsx has the finding, 2026-10-02).
      style={{ position: 'fixed', inset: 0, zIndex: 240, display: 'flex', justifyContent: 'center' }}
      className="items-end bg-black/40 backdrop-blur-sm md:items-start md:py-[14vh]"
    >
      <div aria-hidden className="absolute inset-0" onClick={() => finish(false)} />
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'glass-panel relative flex w-full flex-col overflow-hidden shadow-2xl',
          'max-h-[90vh] rounded-t-2xl',
          'md:w-[min(440px,96vw)] md:rounded-2xl',
        )}
      >
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Mail className="h-3 w-3" /> {t('widgetAppEmail.eyebrow')}
          </div>
          <div className="mt-1 text-sm font-semibold tracking-tight">
            {t('widgetAppEmail.title', { app: req.appName })}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t('widgetAppEmail.body', { address: req.addressHint })}
          </p>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t('widgetAppEmail.stop', { app: req.appName })}
          </p>
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 px-4 py-3 shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_1px_0_rgba(0,0,0,0.35)]">
          <button
            type="button"
            onClick={() => finish(false)}
            className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
          >
            {t('widgetAppEmail.cancel')}
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => finish(true)}
            className="rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground"
          >
            {t('widgetAppEmail.confirm')}
          </button>
        </div>
      </div>
    </PopoverPortal>
  );
}
